import React from 'react';
import { Package, Bell, TrendingUp, CheckCircle } from 'lucide-react';

const ReorderPointAnalysis = ({ data, calculations, formatCurrency }) => {
  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const productCost = parseNum(data?.productCost);
  const quantityNeeded = parseNum(data?.quantityNeeded);
  const leadTimeDays = parseNum(data?.leadTimeDays);
  
  const dailyUsageRate = quantityNeeded > 0 ? quantityNeeded / 30 : 0;
  const leadTimeUsage = Math.ceil(dailyUsageRate * leadTimeDays);
  const recommendedOrderQuantity = quantityNeeded + calculations?.safetyStockUnits;
  const costPerReorder = productCost * recommendedOrderQuantity;

  const getReorderUrgency = () => {
    if (calculations?.reorderPoint <= 0) return { color: 'slate', text: 'Not Set', icon: Package };
    if (calculations?.reorderPoint < quantityNeeded * 0.2) return { color: 'red', text: 'Urgent', icon: Bell };
    if (calculations?.reorderPoint < quantityNeeded * 0.5) return { color: 'amber', text: 'Soon', icon: TrendingUp };
    return { color: 'green', text: 'Optimal', icon: CheckCircle };
  };

  const urgency = getReorderUrgency();
  const UrgencyIcon = urgency?.icon;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-10 h-10 rounded-full bg-${urgency?.color}-100 flex items-center justify-center`}>
          <UrgencyIcon className={`w-5 h-5 text-${urgency?.color}-600`} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">Reorder Point Analysis</h3>
          <p className="text-xs text-slate-600">Inventory optimization & automated alerts</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Reorder Metrics */}
        <div className="space-y-4">
          <h4 className="text-sm font-bold text-slate-900 mb-3">Reorder Metrics</h4>
          
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-600 mb-1">Reorder Point</p>
            <p className={`text-2xl font-bold text-${urgency?.color}-600`}>
              {calculations?.reorderPoint} units
            </p>
            <div className="mt-2 flex items-center gap-2">
              <span className={`px-2 py-1 rounded text-xs font-medium bg-${urgency?.color}-100 text-${urgency?.color}-700`}>
                {urgency?.text}
              </span>
            </div>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-600 mb-1">Safety Stock</p>
            <p className="text-xl font-bold text-slate-900">
              {calculations?.safetyStockUnits} units
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {data?.safetyStockPercent}% buffer
            </p>
          </div>
        </div>

        {/* Lead Time Analysis */}
        <div className="space-y-4">
          <h4 className="text-sm font-bold text-slate-900 mb-3">Lead Time Analysis</h4>
          
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-700 mb-1">Lead Time</p>
            <p className="text-2xl font-bold text-blue-900">
              {leadTimeDays} days
            </p>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-600 mb-1">Daily Usage Rate</p>
            <p className="text-xl font-bold text-slate-900">
              {dailyUsageRate?.toFixed(1)} units/day
            </p>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-600 mb-1">Lead Time Usage</p>
            <p className="text-xl font-bold text-slate-900">
              {leadTimeUsage} units
            </p>
          </div>
        </div>

        {/* Order Recommendations */}
        <div className="space-y-4">
          <h4 className="text-sm font-bold text-slate-900 mb-3">Order Recommendations</h4>
          
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-xs text-green-700 mb-1">Recommended Order Qty</p>
            <p className="text-2xl font-bold text-green-900">
              {recommendedOrderQuantity} units
            </p>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-600 mb-1">Cost Per Reorder</p>
            <p className="text-xl font-bold text-slate-900">
              {formatCurrency(costPerReorder)}
            </p>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-xs text-purple-700 mb-1">Inventory Turnover</p>
            <p className="text-xl font-bold text-purple-900">
              {calculations?.monthlyInventoryTurnover?.toFixed(1)}x
            </p>
            <p className="text-xs text-purple-600 mt-1">per month</p>
          </div>
        </div>
      </div>

      {/* Optimization Recommendations */}
      <div className="mt-6 pt-6 border-t border-slate-200">
        <h4 className="text-sm font-bold text-slate-900 mb-4">Optimization Recommendations</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900 mb-1">Just-in-Time Ordering</p>
                <p className="text-xs text-blue-700">
                  Order {recommendedOrderQuantity} units every {Math.ceil(quantityNeeded / dailyUsageRate)} days to maintain optimal inventory levels
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900 mb-1">Cost Optimization</p>
                <p className="text-xs text-green-700">
                  Negotiate {data?.supplierPaymentTerms}-day payment terms with suppliers to improve cash flow by {formatCurrency(costPerReorder * 0.85)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Automated Alert Setup */}
      <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
        <div className="flex items-start gap-3">
          <Bell className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-900 mb-1">Automated Reorder Alert</p>
            <p className="text-xs text-amber-700">
              System will notify you when inventory reaches {calculations?.reorderPoint} units. 
              This gives you {leadTimeDays} days to place and receive new orders before stock runs out.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReorderPointAnalysis;